<template>

    <div
        :class="{
            'fixed top-0 right-0 bottom-0 left-0 h-full fullscreen': fullscreen,
            'rounded-lg': !fullscreen,
        }"
        class="flex flex-col bg-white canvas::editor-Admin">

        <div v-if="importSuccess" class="p-4 border border-green-200 rounded-md bg-green-50">
            <div class="flex">
                <div class="flex-shrink-0">
                    <svg class="w-5 h-5 text-green-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"
                         fill="currentColor" aria-hidden="true">
                        <path fill-rule="evenodd"
                              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                              clip-rule="evenodd" />
                    </svg>
                </div>
                <div class="ml-3">
                    <div class="text-sm text-green-700">
                        <p>
                            {{ importSuccessMessage }}
                        </p>
                    </div>
                </div>
            </div>
        </div>

        <div
            :class="{
                'opacity-50 pointer-events-none': importingCanvasBlocks,
            }"
        >

            <ToolbarTop
                :activeCanvasVersion="activeCanvasVersion"
                :canvasVersion="canvasVersion"
                :versionState="versionState"
                :activeMode="activeMode"
                :title="title"
                :instruction="instruction"
                :currentLanguageToShow="isMultiLanguage ? currentLanguage : null"
                :fullscreen.sync="fullscreen"
            />

            <div class="relative flex flex-col w-full h-full">

                <div class="flex canvasWrapper">

                    <canvas-component
                        ref="canvasComponent"
                        :primaryLanguage="primaryLanguage"
                        :language="language"
                        :canvasVersion="canvasVersion"
                        :activeMode="activeMode"
                        :activeViewport="activeViewport"
                        :stateSaving="stateSaving"
                        :galleryDraggingBlock="galleryDraggingBlock"
                        @loaded="iframeLoaded = true"
                        @importCompleted="handleImportCompleted"
                        @importing="importingCanvasBlocks = $event"
                    />

                    <gallery
                        v-if="activeMode == 'overview'"
                        :canvasBlocksGalleryData="canvasBlocksGalleryData"
                        :canvasVersion="canvasVersion"
                        :canvasId="id"
                        @onDraggingBlock="galleryDraggingBlock = $event"
                        @addBlock="$refs['canvasComponent'].pageAddBlock($event)"
                    />

                </div>

                <div class="sticky bottom-0 z-10 py-3 my-0 text-right bg-white toolbarBottom" :class="{ loading: !iframeLoaded || stateSaving }">
                    <div class="flex py-0">

                        <ModeAndViewportToolbar
                            :activeMode.sync="activeMode"
                            :activeViewport.sync="activeViewport"
                        />

                        <VersionActions
                            :canvas-version="canvasVersion"
                            :version-state="versionState"
                            :useVersioning="useVersioning"
                            :currentLanguage="currentLanguage"
                            @update.canvasVersion="setNewLanguageCanvasVersion($event)"
                            @onNewCanvasVersions="setNewCanvasVersions($event)"
                            @canvasVersionBackToLive="canvasVersionBackToLive"
                        />

                    </div>
                </div>

            </div>

        </div>

    </div>

</template>

<script>

import Canvas from "./admin/Canvas";
import Gallery from "./admin/Gallery";
import VersionActions from "./admin/VersionActions";
import ModeAndViewportToolbar from "./admin/ModeAndViewportToolbar";
import ToolbarTop from "./admin/ToolbarTop";

export default {

    provide: function ()
    {
        return {
            editorComponent: this,
        };
    },

    components: {
        ModeAndViewportToolbar,
        'canvas-component': Canvas,
        Gallery,
        ToolbarTop,
        VersionActions,
    },

    props: {
        id: String,
        title: String,
        instruction: String,

        primaryLanguage: String,
        language: String,
        canvasVersions: Object,
        canvasBlocksGalleryData: Array,

        useVersioning: Boolean,
    },

    data()
    {
        return {
            activeMode: null,
            activeViewport: 'xl',
            canvasVersionsLocal: this.canvasVersions,
            localVersionState: null,
            iframeLoaded: false,
            stateSaving: false, // Manipulated else where.
            galleryDraggingBlock: null,
            currentLanguage: this.language || Object.keys(this.canvasVersions)[0],
            fullscreen: false,
            importSuccess: false,
            importSuccessMessage: '',
            importingCanvasBlocks: false,
        };
    },

    mounted()
    {
        this.initActiveVersion();
        this.initInstance();
        this.initVersionState();
    },

    methods: {

        handleImportCompleted(canvasId) {
            // Initialize the success message immediately
            this.importSuccessMessage = "De blokken zijn succesvol geimporteerd. De pagina wordt herladen in 3 seconden...";
            this.importSuccess = true;

            // Start the countdown
            let countdown = 3;
            const interval = setInterval(() => {
                countdown--;

                // Update the message during the countdown
                this.importSuccessMessage = `De blokken zijn succesvol geimporteerd. De pagina wordt herladen in ${countdown} seconden...`;

                if (countdown === 0) {
                    clearInterval(interval);

                    window.location.reload();
                }
            }, 1000);
        },

        initActiveVersion()
        {
            // To overview due to no canvas blocks present yet?
            this.activeMode = this.canvasVersion.canvas_blocks.length > 0 ? 'content' : 'overview';
        },

        initInstance()
        {
            // @ugly Set so language switcher can reach it.
            $(this.$el).data('vueInstance', this);
        },

        initVersionState()
        {
            // @ugly TODO Vue anti pattern.
            this.$watch('$refs.canvasComponent.$refs.iframe.iframeApp', () =>
            {
                // @ugly TODO Vue anti pattern.
                let iframeApp = this.$refs.canvasComponent.$refs.iframe.iframeApp;

                // Iframe app present? May be gone while iframe is loading for another language.
                if (iframeApp)
                    iframeApp.$watch('pageComponent.state.dirty', () =>
                    {
                        this.localVersionState = 'concept';
                    });
            });
        },

        setNewLanguageCanvasVersion(canvasVersion)
        {
            // Set
            this.canvasVersionsLocal[this.currentLanguage].canvasVersion = canvasVersion;
        },

        setNewCanvasVersions(canvasVersions)
        {
            // Set
            this.canvasVersionsLocal = canvasVersions;
        },

        canvasVersionBackToLive()
        {
            // Clear overwriteable version state. Computed this.versionState() will check what is right.
            this.localVersionState = null

            // TODO High: Refresh iframe.

        },

        scrollInViewport()
        {
            // Determine scrolltop.
            let scrollTop = $(this.$el).offset().top - 30;

            // Scroll
            $('html, body')
                .scrollTop(scrollTop);
        },

    },

    computed: {

        canvasComponent()
        {
            return this.$refs['canvasComponent'];
        },

        versionState()
        {
            // Overwrite?
            if (this.localVersionState)
                return this.localVersionState;

            // Check version.
            return this.canvasVersionsLocal[this.currentLanguage].activeCanvasVersion?.id && this.canvasVersion.created_at == this.canvasVersion.updated_at ? 'live' : 'concept';
        },

        canvasVersion()
        {
            return this.canvasVersionsLocal[this.currentLanguage].canvasVersion;
        },

        activeCanvasVersion()
        {
            return this.canvasVersionsLocal[this.currentLanguage].activeCanvasVersion;
        },

        isMultiLanguage()
        {
            return Object.values(this.canvasVersionsLocal).length > 1;
        },

    },

    watch: {

        'fullscreen'(newVal)
        {
            // Scroll due to fullscreen ended? We have to wait for the next redraw.
            if (!newVal)
                this.$nextTick(() => this.scrollInViewport());
        },

    }

}

</script>

<style lang="scss" scoped>

.canvas\:\:editor-Admin {
    @apply bg-white;
    z-index: 100;

    //&:not(.fullscreen) {
    //    min-height: calc(100vh - 75px);
    //}

    .toolbarBottom {
        border-top: 1px solid #ddd !important;
        transition: opacity .2s;

        &.loading {
            opacity: .5;
            pointer-evens: none;
        }
    }

    .canvasWrapper {
        min-height: 440px;
    }
}

</style>
